import { logError } from 'client/javascript/util/logger';
import {
  setupScrollAnalyticsTracking,
  setupScrollDistanceAnalyticsTracking,
  trackInteraction
} from 'client/javascript/analytics';

const SCROLL_PERCENT = 75;

export const INTERACTION_LABELS = {
  clickHeroSectionViewAll: 'Click - View All in Main Header',
  clickMediaText1AcrossStandardModuleOptions: 'Click - Link in Convenient Locations Module',
  startToPlayVideo: 'Click - Video Play',
  changeMediaText1AcrossStandardModuleCarouselSlide: 'Click - Image Carousel',
  scrollToForm: 'Scroll - Viewed Forms',
  clickWorkspaceNearYouModule: 'Click - "Workspace near you" building card',
  clickWorkspaceNearYouModuleViewAllBuildings: 'Click - View All Buildings in "Workspace near you" Module',
  scrollToFAQ: 'Scroll - Viewed FAQ',
  expandFAQItem: 'Click - Expand FAQ',
  closeFAQItem: 'Click - Close FAQ',
  clickStickyCTABarModule: 'Click - Sticky CTA',
  scrollOver: `Scroll Over ${SCROLL_PERCENT}%`,
  scrollToFooter: 'Footer - Viewed',
  scrollToComparisons: 'Scroll - Viewed Comparisons',
  clickCompareSolutionsCTAButton: 'Click - Compare solutions CTA Button'
};

export const PRODUCT_PAGE_MODULES = {
  moduleHeroFeaturedPdp: 'section.hero-featured',
  moduleMediaText1AcrossStandard: '.media-text-one-across-item',
  moduleWorkspaceNearYou: '.nearby-buildings',
  moduleFaq: 'section.faq',
  moduleCheckoutStickyBar: '.sticky-bar',
  moduleSearchStickyBar: '#panel_button_filter',
  moduleCompareSolutions: '#compare-module'
};

export const SELECTORS = {
  viewAll: '.hero-featured .open-amenities-button',
  mediaTextModuleLink: '.ray-link--with-arrow',
  nearbyBuildingsViewAllLink: '.nearby-buildings__link--desktop a',
  nearbyBuildingsViewAllCard: '.nearby-buildings-slider__card--link',
  faqItem: '.faq__summary',
  dynamicForm: '.dynamic-form',
  checkoutStickyBarCTA: '.sticky-bar__btn',
  searchStickyBarCTA: '.pannel__button',
  footerBlock: '.page-footer',
  compareSolutionsCTAButton: '.compare-module__table-cta-button'
};

const setupHeroSectionModule = (pageSelector = document) => {
  const heroSectionBlock = pageSelector.querySelector(PRODUCT_PAGE_MODULES.moduleHeroFeaturedPdp);

  if (heroSectionBlock) {
    heroSectionBlock
      .querySelector(SELECTORS.viewAll)
      .addEventListener('click', () => trackInteraction({ label: INTERACTION_LABELS.clickHeroSectionViewAll }));
  }
};

const setupMediaText1AcrossStandardModule = (pageSelector = document) => {
  pageSelector.querySelectorAll(PRODUCT_PAGE_MODULES.moduleMediaText1AcrossStandard).forEach(module => {
    if (module) {
      const moduleLink = module.querySelector(SELECTORS.mediaTextModuleLink);

      if (moduleLink)
        moduleLink.addEventListener('click', ({ currentTarget }) =>
          trackInteraction({
            label: INTERACTION_LABELS.clickMediaText1AcrossStandardModuleOptions,
            toPath: currentTarget.href
          })
        );
    }
  });
};

export const setupMediaTextOneAcrossModuleSliders = sliders => {
  if (sliders.length)
    sliders.forEach(({ glide }, galleryIndex) =>
      glide.on('run', () =>
        trackInteraction({
          label: `${INTERACTION_LABELS.changeMediaText1AcrossStandardModuleCarouselSlide} ${galleryIndex}`,
          sliderImageIndex: glide.index
        })
      )
    );
};

const setupWorkspaceNearYouModule = (pageSelector = document) => {
  const workspaceNearYouBlock = pageSelector.querySelector(PRODUCT_PAGE_MODULES.moduleWorkspaceNearYou);

  if (workspaceNearYouBlock) {
    workspaceNearYouBlock
      .querySelector(SELECTORS.nearbyBuildingsViewAllLink)
      .addEventListener('click', ({ currentTarget }) =>
        trackInteraction({
          label: INTERACTION_LABELS.clickWorkspaceNearYouModuleViewAllBuildings,
          toPath: currentTarget.href
        })
      );
  }
};

export const setupWorkspaceNearYouModuleSlider = (pageSelector = document) => {
  const workspaceNearYouBlock = pageSelector.querySelector(PRODUCT_PAGE_MODULES.moduleWorkspaceNearYou);

  if (workspaceNearYouBlock) {
    workspaceNearYouBlock.querySelectorAll(SELECTORS.nearbyBuildingsViewAllCard).forEach(card => {
      card.addEventListener('click', ({ currentTarget }) =>
        trackInteraction({
          label: INTERACTION_LABELS.clickWorkspaceNearYouModule,
          location_uuid: currentTarget.getAttribute('data-building-id')
        })
      );
    });
  }
};

const setupFAQModule = (pageSelector = document) => {
  const faqBlock = pageSelector.querySelector(PRODUCT_PAGE_MODULES.moduleFaq);

  if (faqBlock) {
    setupScrollAnalyticsTracking({ label: INTERACTION_LABELS.scrollToFAQ, selector: PRODUCT_PAGE_MODULES.moduleFaq });

    faqBlock.querySelectorAll(SELECTORS.faqItem).forEach((faqItem, indexItem) => {
      faqItem.addEventListener('click', () => {
        const isOpen = faqItem.closest('.faq__details').hasAttribute('open');

        trackInteraction({
          label: isOpen ? INTERACTION_LABELS.closeFAQItem : INTERACTION_LABELS.expandFAQItem,
          selectionName: indexItem
        });
      });
    });
  }
};

const setupStickyBar = (pageSelector = document) => {
  const checkoutStickyBar = pageSelector.querySelector(PRODUCT_PAGE_MODULES.moduleCheckoutStickyBar);

  if (checkoutStickyBar) {
    checkoutStickyBar.querySelector(SELECTORS.checkoutStickyBarCTA).addEventListener('click', ({ currentTarget }) =>
      trackInteraction({
        label: INTERACTION_LABELS.clickStickyCTABarModule,
        toPath: currentTarget.href
      })
    );
  }
};

const setupCompareSolutionsModule = (pageSelector = document) => {
  const compareSolutionsSelector = pageSelector.querySelector(PRODUCT_PAGE_MODULES.moduleCompareSolutions);

  if (compareSolutionsSelector) {
    setupScrollAnalyticsTracking({
      label: INTERACTION_LABELS.scrollToComparisons,
      selector: PRODUCT_PAGE_MODULES.moduleCompareSolutions
    });
    compareSolutionsSelector.querySelectorAll(SELECTORS.compareSolutionsCTAButton).forEach((button, index) => {
      button.addEventListener('click', () => {
        const titles = button.closest('tbody').querySelectorAll('.compare-module__table-title');

        trackInteraction({
          label: INTERACTION_LABELS.clickCompareSolutionsCTAButton,
          selectionName: titles[index].textContent
        });
      });
    });
  }
};

export const handleSearchStickyBarIteration = href =>
  trackInteraction({
    label: INTERACTION_LABELS.clickStickyCTABarModule,
    toPath: href
  });

const initScrollTracking = () => {
  const formElement = document.querySelector(SELECTORS.dynamicForm);
  const footerElement = document.querySelector(SELECTORS.footerBlock);

  if (formElement) {
    setupScrollAnalyticsTracking({ label: INTERACTION_LABELS.scrollToForm, selector: SELECTORS.dynamicForm });
  }
  if (footerElement) {
    setupScrollAnalyticsTracking({ label: INTERACTION_LABELS.scrollToFooter, selector: SELECTORS.footerBlock });
  }
  setupScrollDistanceAnalyticsTracking([{ label: INTERACTION_LABELS.scrollOver, breakpoint: SCROLL_PERCENT }]);
};

export const setupAnalytics = (glides = []) => {
  try {
    const productDetailsPage = document.querySelector('.product-details');

    setupHeroSectionModule(productDetailsPage);
    setupMediaText1AcrossStandardModule(productDetailsPage);
    setupWorkspaceNearYouModule(productDetailsPage);
    setupMediaTextOneAcrossModuleSliders(glides);
    setupFAQModule(productDetailsPage);
    setupStickyBar(productDetailsPage);
    setupCompareSolutionsModule(productDetailsPage);
    initScrollTracking();
  } catch (error) {
    logError(`PDP page analytics failed: ${error.message}`, error);
  }
};
